import type { ChatMessage, ChatMessagePart, ChatToolInvocationState } from '@mui/x-chat/types';
import {
  AGENTIC_PLATFORM_ASK_USER_TOOL_NAME,
  type AgenticPlatformAssistantMessage,
  type AgenticPlatformMessage,
  type AgenticPlatformUserMessage,
} from 'agentic-platform-schema';

import { isDefined, isTruthy } from 'common';

import { parseJsonOrFallbackToValue } from '../../../utils/json';
import { getAgenticChatQuestionPart, parseAgenticChatQuestion } from '../Question/utils';

interface ToolResult {
  errorText: string | undefined;
  output: unknown;
}

function getToolResultsByCallId(messages: AgenticPlatformMessage[]): Map<string, ToolResult> {
  const resultsByCallId = new Map<string, ToolResult>();
  for (const message of messages) {
    if (message.role === 'tool') {
      resultsByCallId.set(message.toolCallId, {
        errorText: message.error,
        output: parseJsonOrFallbackToValue(message.content),
      });
    }
  }
  return resultsByCallId;
}

/** A call with no result at all belongs to a run that was interrupted before it reported one. */
function getToolInvocationState(result: ToolResult | undefined): ChatToolInvocationState {
  if (!isDefined(result)) {
    return 'input-available';
  }
  return isDefined(result.errorText) ? 'output-error' : 'output-available';
}

function getMessageParts(options: {
  message: AgenticPlatformAssistantMessage | AgenticPlatformUserMessage;
  toolResultsByCallId: Map<string, ToolResult>;
}): ChatMessagePart[] {
  const { message, toolResultsByCallId } = options;
  const text =
    typeof message.content === 'string'
      ? message.content
      : (message.content ?? []).map((part) => part.text ?? '').join('');
  const textParts: ChatMessagePart[] = isTruthy(text.length) ? [{ text, type: 'text' }] : [];
  const toolParts: ChatMessagePart[] = (
    message.role === 'assistant' ? (message.toolCalls ?? []) : []
  ).map((toolCall) => {
    const result = toolResultsByCallId.get(toolCall.id);
    const input = parseJsonOrFallbackToValue(toolCall.function.arguments);
    if (toolCall.function.name === AGENTIC_PLATFORM_ASK_USER_TOOL_NAME) {
      const question = parseAgenticChatQuestion(input);
      if (isDefined(question)) {
        return getAgenticChatQuestionPart({ id: toolCall.id, question });
      }
    }
    return {
      toolInvocation: {
        input,
        state: getToolInvocationState(result),
        toolCallId: toolCall.id,
        toolName: toolCall.function.name,
        ...(isDefined(result) ? { output: result.output } : {}),
        ...(isDefined(result?.errorText) ? { errorText: result.errorText } : {}),
      },
      type: 'dynamic-tool',
    };
  });
  return [...textParts, ...toolParts];
}

/**
 * Rebuilds a chat transcript from a session's stored conversation.
 *
 * Tool results arrive as their own messages, mirroring how a model sees them, but the chat
 * renders a call and its result as one part, so results are folded back into the assistant
 * message that requested them.
 *
 * A run that used a tool is stored as several assistant entries around the tool results it
 * waited on. The live stream projects one run onto one assistant message, so the entries after a
 * user message are replayed as one message too, and reloading leaves the transcript unchanged.
 */
export function getReplayedChatMessages(messages: AgenticPlatformMessage[]): ChatMessage[] {
  const toolResultsByCallId = getToolResultsByCallId(messages);
  const replayedMessages: ChatMessage[] = [];
  let assistantTurn:
    | {
        createdAt: string | undefined;
        id: string;
        parts: ChatMessagePart[];
      }
    | undefined;

  function endAssistantTurn() {
    if (isDefined(assistantTurn) && isTruthy(assistantTurn.parts.length)) {
      replayedMessages.push({ ...assistantTurn, role: 'assistant' });
    }
    assistantTurn = undefined;
  }

  for (const message of messages) {
    if (message.role === 'user') {
      endAssistantTurn();
      const parts = getMessageParts({ message, toolResultsByCallId });
      if (isTruthy(parts.length)) {
        replayedMessages.push({
          createdAt: message.timestamp,
          id: message.id,
          parts,
          role: 'user',
        });
      }
      continue;
    }
    if (message.role === 'assistant') {
      // A run's later entries are written as it progresses, so the turn is dated when it began.
      assistantTurn ??= { createdAt: message.timestamp, id: message.id, parts: [] };
      assistantTurn.parts.push(...getMessageParts({ message, toolResultsByCallId }));
    }
  }
  endAssistantTurn();

  return replayedMessages;
}
